/**
 * BUILD YOUR 2-PACK.
 *
 * ONE GLOBAL STAGE AT A TIME. The page holds a single stage — designs, packages,
 * personalisation, review — and renders only that stage's controls. An earlier
 * version let the header say "step 1" while a cup already showed step 2; the
 * stage now lives in one place and every panel derives from it.
 *
 * There is no product form on this page, and that absence is deliberate: no
 * product form means Shopify never renders the accelerated checkout button, so
 * the Buy-with-Shop bypass that produced bad orders on 2026-09-01 cannot happen
 * here. The whole configuration is POSTed to /cart/add.js instead, which is the
 * only path that carries the line item properties fulfilment needs.
 *
 * THE CART CONTRACT, and why it is spelled out three ways per choice.
 * A Full CrewCup Kit is a Simple Bundles bundle. Its own widget submits, and the
 * app's backend reads:
 *
 *   properties[<raw selector id>]   the value, under the app's own long id —
 *                                   this is the one the app actually matches on
 *   properties[<readable label>]    a duplicate a human can read on the order
 *   properties[_bundle_selection]   every chosen value joined by " <> "
 *
 * Sending fewer than all three has failed twice before: a Rebuy widget added a
 * kit variant with none of them, and a race on the product page dropped them
 * mid-selection. Both produced orders that looked right and shipped wrong. The
 * payload is therefore VALIDATED before it is sent — Shopify silently drops
 * properties with empty values, so a blank charm would vanish without trace.
 *
 * A Cup Only line carries no properties at all.
 *
 * @typedef {object} Variant
 * @property {string} id
 * @property {string} title
 * @property {string} price - Decimal string from products.json, e.g. "21.99".
 * @property {string | null} image - The variant's own featured image, when it has one.
 * @property {string | null} compareAt - Was-price, when the variant has one.
 * @property {boolean} available
 * @property {boolean} isKit
 *
 * @typedef {object} Design
 * @property {string} handle
 * @property {string} title
 * @property {string | null} image
 * @property {string[]} images - Every gallery image, for the media column.
 * @property {Variant[]} variants
 *
 * @typedef {object} BundleValue
 * @property {string} value
 * @property {string | null} image
 *
 * @typedef {object} BundleSelector
 * @property {string} name - The app's selector id, submitted as properties[<name>].
 * @property {string} label - Readable label, submitted as properties[<label>].
 * @property {string} [product_title] - The component's product, used to qualify a generic label.
 * @property {BundleValue[]} values
 *
 * @typedef {object} Bundle
 * @property {boolean} configured - False when the bundle is not registered in Simple Bundles yet.
 * @property {number} kit_variant_id
 * @property {BundleSelector[]} selectors
 */
import { lockScroll, unlockScroll } from '@theme/utilities';
import { formatMoney, convertMoneyToMinorUnits } from '@theme/money-formatting';
import { CartLinesUpdateEvent } from '@shopify/events';

/** Words that make a generic option label meaningful, read from the component's product title. */
const QUALIFIERS = /\b(Boot|Charm|Lid|Straw|Sticker|Brush|Handle)\b/i;

/** Simple Bundles' own delimiter between selector values. */
const BUNDLE_GROUP_SEPARATOR = ' <> ';

/**
 * The largest pack the markup can hold. The section renders this many cup
 * panels and leaves the surplus inert, because the pack size can change without
 * a page load — Liquid cannot read a query string, and the size switcher has to
 * respond to a tap rather than a reload.
 */
const MAX_CUPS = 6;

class TwoPackBuilder extends HTMLElement {
  /** @type {Design[]} */
  #designs = [];

  /**
   * How many cups this pack holds. Settled in #resolvePackSize from, in order of
   * precedence: a ?cups= query parameter, the page handle, then the section
   * setting. Never above MAX_CUPS, because only that many panels exist.
   *
   * @type {number}
   */
  #packSize = 2;

  /** The cup numbers in play, 1..#packSize. */
  get #cups() {
    return Array.from({ length: this.#packSize }, (_, i) => i + 1);
  }

  /**
   * Per-cup state.
   *
   * The package is stored as a TIER, not a variant id, because a shopper may
   * choose it before choosing a design — and because the variant that tier means
   * changes with the design. Resolving it late (see #variantFor) is what lets
   * the two choices be made in either order without ever holding a variant id
   * that belongs to a different product.
   *
   * @type {Map<number, { handle: string | null, tier: 'full' | 'single' | null, addons: Map<string, string> }>}
   */
  #choice = new Map();

  /**
   * Per-design bundle data, keyed by handle. Cached so flicking between designs
   * does not refetch the same thing.
   * @type {Map<string, Bundle>}
   */
  #bundles = new Map();

  /**
   * Which cup is being configured.
   *
   * Replaces the four-stage wizard: the product page configures one cup with
   * everything on screen at once, and this page does that twice — so the only
   * navigation is which cup you are on, not which step.
   * @type {number}
   */
  #tab = 1;

  /** @type {ReturnType<typeof setTimeout> | null} Clears the confirmation message. */
  #toastTimer = null;

  /**
   * Which gallery image is showing, per cup — so switching tabs returns to the
   * image that cup was left on rather than resetting to the first.
   * @type {Map<number, string>}
   */
  #activeImage = new Map();

  /** @type {number | null} */
  #activeCup = null;

  /** @type {HTMLElement | null} Where to send focus when a dialog closes. */
  #returnFocusTo = null;

  #loaded = false;
  #busy = false;

  connectedCallback() {
    this.#resolvePackSize();

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onInput);

    const picker = this.#dialog('[data-picker]');
    if (picker) {
      picker.addEventListener('close', () => {
        unlockScroll(picker);
        this.#activeCup = null;
        // Native <dialog> returns focus to whatever was focused when
        // showModal() ran — but the button that opened this is often replaced by
        // the re-render that follows a choice, so the browser has nowhere to put
        // it. Send focus somewhere meaningful instead of the top of the page.
        this.#restoreFocus();
      });
      // The backdrop is not its own element: a click on it lands on the dialog
      // itself, while any click inside lands on a descendant. Only sound because
      // the dialog carries no padding of its own.
      picker.addEventListener('click', (event) => {
        if (event.target === picker) picker.close();
      });
    }

    const video = this.#dialog('[data-video-dialog]');
    if (video) {
      video.addEventListener('close', () => {
        unlockScroll(video);
        this.#restoreFocus();
        // Tear the player down so it cannot keep playing behind a closed dialog.
        const slot = video.querySelector('[data-video-slot]');
        if (slot) slot.replaceChildren();
      });
      video.addEventListener('click', (event) => {
        if (event.target === video) video.close();
      });
    }

    // A lock must never survive a navigation. Horizon uses view transitions, so
    // this element can be torn down without `close` ever firing.
    window.addEventListener('resize', this.#syncRails, { passive: true });

    window.addEventListener('pagehide', () => {
      if (picker) unlockScroll(picker);
      if (video) unlockScroll(video);
    });

    // NEITHER package is preselected. The cards are on screen with their real
    // prices and badges from the first paint, but nothing is ticked — a coral,
    // checked kit card reads as a decision already made, on a page where the
    // shopper has not decided anything yet. The product page can default to the
    // kit because it has only one thing to configure; here it looked like a cup
    // was already chosen.
    // Seeded for every panel that exists, not just the current pack size, so
    // growing the pack finds state waiting and shrinking then growing again
    // returns the shopper's earlier cups rather than blank ones.
    for (let cup = 1; cup <= MAX_CUPS; cup += 1) {
      this.#choice.set(cup, { handle: null, tier: null, addons: new Map() });
    }

    // Loaded up front rather than when the picker opens: the package cards show
    // real prices from a reference design before any design is chosen, and they
    // cannot do that with no data.
    this.#load().then(() => this.#render());

    this.#render();
  }

  /* ------------------------------------------------------------ elements */

  /* ------------------------------------------------------------ pack size */

  /**
   * How many cups this pack holds, in order of precedence:
   *
   *   1. ?cups=N          — for testing a size without making a page
   *   2. the page handle   — "build-your-3-pack" is a 3-pack, so adding a size
   *                          later is a page in admin and no deploy
   *   3. the section setting
   *
   * The handle is read server-side into data-default-pack-size, because Liquid
   * has no access to a query string; the parameter is applied here on top of it.
   *
   * That attribute is deliberately NOT called data-pack-size: the size buttons
   * use that name, and a host element sharing it made [data-pack-size] match the
   * builder itself — so a click on any unhandled part of the page reset the pack
   * to its default size.
   *
   * An unrecognised value falls back rather than breaking — renaming a page
   * must not take its builder down.
   */
  #resolvePackSize() {
    const fromSetting = Number(this.dataset.defaultPackSize);
    let size = Number.isFinite(fromSetting) && fromSetting > 0 ? fromSetting : 2;

    const asked = Number(new URLSearchParams(location.search).get('cups'));
    if (Number.isFinite(asked) && asked > 0) size = asked;

    this.#packSize = Math.min(Math.max(Math.round(size), 1), MAX_CUPS);
  }

  /**
   * Change the pack size in place.
   *
   * No reload: the surplus panels are already in the markup, so this is a
   * re-render. Per-cup state is kept rather than cleared — a shopper who taps
   * 4, configures three cups, then drops to 2 and back to 4 has not lost the
   * work. Only cups inside the new size are ever read, by #cups.
   *
   * @param {number} size
   */
  #setPackSize(size) {
    const next = Math.min(Math.max(Math.round(size), 1), MAX_CUPS);
    if (next === this.#packSize) return;

    this.#packSize = next;
    // The active tab may no longer exist.
    if (this.#tab > next) this.#tab = next;

    // A shrink leaves stale summaries in panels that are no longer part of the
    // pack; they are hidden, but the review column reads from every slot.
    for (let cup = next + 1; cup <= MAX_CUPS; cup += 1) {
      const slot = this.#slot('review', cup);
      if (slot instanceof HTMLElement) slot.replaceChildren();
    }

    this.#render();
  }

  /**
   * Fills the [n] token in any merchant string with the pack size, so one set
   * of wording serves every pack: "Build Your [n]-Pack" reads correctly at 2 or
   * at 4. A string with no token is returned untouched, which is what keeps the
   * existing 2-pack page — whose settings say "2-Pack" literally — unchanged.
   *
   * @param {string} text
   */
  #packText(text) {
    return text
      .replaceAll('[cups]', this.#cupsPhrase)
      .replaceAll('[pack]', this.#packPhrase)
      .replaceAll('[n]', String(this.#packSize));
  }

  /** "1 cup" / "3 cups" — the size and the cup word, agreeing in number. */
  get #cupsPhrase() {
    const word = this.#cupWord.toLowerCase();
    return `${this.#packSize} ${word}${this.#packSize === 1 ? '' : 's'}`;
  }

  /**
   * How to name the pack. "3-Pack" reads well; "1-Pack" does not, and the
   * homepage links straight to a single-cup builder, so that case is real.
   */
  get #packPhrase() {
    return this.#packSize === 1 ? this.#cupWord : `${this.#packSize}-Pack`;
  }

  /**
   * What this pack costs, per cup, for BOTH packages — before anything is
   * chosen.
   *
   * Until now the shopper had to take the homepage's word for the price and
   * start configuring before any number appeared. This states it plainly:
   * a Single at this pack size, a Full Kit at this pack size, and the saving.
   *
   * Priced from the reference design, the same prices the package cards already
   * show, so the two cannot disagree. Hidden at a pack of one, where there is
   * no discount to describe, and hidden outside the primary currency, where
   * #packSaving refuses to guess (see its note).
   */
  #renderPackDeal() {
    const host = this.querySelector('[data-pack-deal]');
    if (!(host instanceof HTMLElement)) return;

    const ref = this.#referenceDesign;
    // Straight from the ladder, not from #packSaving: that counts only cups
    // whose package is chosen, which is none of them when this block matters
    // most — the moment the page loads.
    const off = this.#discountPerCup();
    const single = ref?.variants.find((v) => !v.isKit);
    const kit = ref?.variants.find((v) => v.isKit);

    if (this.#packSize < 2 || !single || !kit) {
      host.hidden = true;
      host.replaceChildren();
      return;
    }

    if (off <= 0) {
      // OUTSIDE THE PRIMARY CURRENCY the amounts cannot be stated: the ladder is
      // written in the shop's own money and Shopify converts the real discount
      // at its own rate, which this page has no way to know. Saying nothing at
      // all reads as a broken page, though, so say the true part without
      // inventing a number. A missing ladder entry in the primary currency
      // genuinely means no discount, so that still hides.
      const foreign =
        this.dataset.currency &&
        this.dataset.primaryCurrency &&
        this.dataset.currency !== this.dataset.primaryCurrency;
      const note = this.dataset.dealOther;

      if (foreign && note) {
        const p = document.createElement('p');
        p.className = 'packdeal__save';
        p.textContent = this.#packText(note);
        host.replaceChildren(p);
        host.hidden = false;
      } else {
        host.hidden = true;
        host.replaceChildren();
      }
      return;
    }

    /**
     * @param {string} name
     * @param {number} base
     */
    const row = (name, base) => {
      const each = Math.max(base - off, 0);
      const line = document.createElement('p');
      line.className = 'packdeal__row';

      const who = document.createElement('span');
      who.className = 'packdeal__who';
      who.textContent = name;

      const rate = document.createElement('span');
      rate.className = 'packdeal__rate';
      rate.textContent = `${this.#money(each.toFixed(2))} a ${this.#cupWord.toLowerCase()}`;

      const sum = document.createElement('span');
      sum.className = 'packdeal__sum';
      sum.textContent = `${this.#money((each * this.#packSize).toFixed(2))} for ${this.#packSize}`;

      const was = document.createElement('s');
      was.className = 'packdeal__was';
      was.textContent = this.#money((base * this.#packSize).toFixed(2));

      line.append(who, rate, sum, was);
      return line;
    };

    const save = document.createElement('p');
    save.className = 'packdeal__save';
    save.textContent = `${this.dataset.saveLabel || 'You save'} ${this.#money(
      (off * this.#packSize).toFixed(2)
    )} on ${this.#cupsPhrase}`;

    host.replaceChildren(
      row(this.dataset.singleLabel || 'Single CrewCup', Number(single.price)),
      row(kit.title, Number(kit.price)),
      save
    );
    host.hidden = false;
  }

  /**
   * The per-cup discount for the current pack size, in the cart's currency, or
   * 0 when it cannot be stated. Shares the currency guard with #packSaving.
   */
  #discountPerCup() {
    const currency = this.dataset.currency;
    const primary = this.dataset.primaryCurrency;
    if (!currency || !primary || currency !== primary) return 0;

    let perCup = 0;
    for (const pair of (this.dataset.packDiscounts || '').split(',')) {
      const [size, amount] = pair.split(':').map((x) => x.trim());
      if (Number(size) === this.#packSize) perCup = Number(amount);
    }
    return Number.isFinite(perCup) && perCup > 0 ? perCup : 0;
  }

  /** Rewrites the headings that name the pack size. */
  #renderPackText() {
    // "Mix & match any 1 designs" was the badge at a pack of one, and no token
    // fixes it — you cannot mix and match a single cup. Copy that only makes
    // sense for a real pack says so, and is hidden at one.
    for (const el of this.querySelectorAll('[data-pack-plural-only]')) {
      if (el instanceof HTMLElement) el.hidden = this.#packSize < 2;
    }

    for (const el of this.querySelectorAll('[data-pack-text]')) {
      if (!(el instanceof HTMLElement)) continue;
      // The original is kept, because a re-render after a size change must
      // substitute into the template rather than into last render's output.
      el.dataset.packTemplate ??= el.textContent ?? '';
      el.textContent = this.#packText(el.dataset.packTemplate);
    }
  }

  /**
   * What this pack saves, in the cart's currency, or 0 when nothing can be
   * promised.
   *
   * The theme cannot ask Shopify what an automatic discount will do before the
   * cart exists, so the ladder is duplicated in a setting. That makes drift the
   * hazard: change the discount in admin without changing the setting and this
   * page promises a price the cart will not charge. The setting's own help text
   * says so, and the amount is deliberately expressed the same way Shopify
   * applies it — a fixed amount off EACH cup — so the two cannot disagree by
   * construction, only by neglect.
   *
   * THE CURRENCY GUARD IS THE IMPORTANT PART. The setting is written in the
   * shop's own currency. A shopper in another market sees converted prices, and
   * subtracting 4.99 US dollars from a peso total then formatting it with a peso
   * sign produces a confident, wrong number — the exact bug shipped once before
   * on the ABConvert price display. Outside the primary currency this returns 0,
   * so the page shows the honest full price and the discount appears in the cart
   * where Shopify has done the conversion itself.
   */
  #packSaving() {
    const perCup = this.#discountPerCup();
    if (perCup <= 0) return 0;

    // Only cups with a package chosen have a price to discount.
    const priced = this.#cups.filter((c) => this.#variantFor(c) != null).length;
    return perCup * priced;
  }

  /**
   * The next cup still needing work, or null when the pack is finished.
   *
   * This replaced four copies of `cup === 1 ? 2 : 1`, which silently meant "the
   * other one" and only made sense in a pack of two — at three cups it sent the
   * shopper back to cup 1 and the flow could never reach the cart.
   *
   * Cups after this one come first so the pack is built in order; earlier cups
   * are then swept up, which matters when a shopper jumps back, fixes cup 1, and
   * cup 3 is still empty.
   *
   * @param {number} cup
   * @returns {number | null}
   */
  #nextUnfinished(cup) {
    const after = this.#cups.filter((c) => c > cup && !this.#isComplete(c));
    if (after.length) return after[0];
    const before = this.#cups.filter((c) => c < cup && !this.#isComplete(c));
    return before.length ? before[0] : null;
  }

  /** @param {string} selector */
  #dialog(selector) {
    const el = this.querySelector(selector);
    return el instanceof HTMLDialogElement ? el : null;
  }

  /**
   * @param {string} slot
   * @param {number} cup
   */
  #slot(slot, cup) {
    return this.querySelector(`[data-slot="${slot}"][data-cup="${cup}"]`);
  }

  /* ---------------------------------------------------------------- data */

  /**
   * Designs come from the collection's products.json rather than from Liquid.
   *
   * `{% for product in collection.products %}` stops at 50 items unless
   * paginated, and this collection holds 165 buyable products carrying the
   * Package option — a Liquid-rendered list would silently show only the first
   * third, which reads as "designs are missing" rather than as a bug.
   */
  async #load() {
    if (this.#loaded) return;

    const handle = this.dataset.collection ?? '';
    const state = this.querySelector('[data-picker-state]');
    if (state) state.textContent = 'Loading designs…';

    try {
      // THE MARKET IS PINNED, and it matters.
      //
      // products.json is market-aware: the same cup is 23.99 in the US and
      // 1600.00 in the Philippines. The money FORMAT, though, is rendered into
      // this page by Liquid when the market was resolved server-side. Fetching
      // without a country lets the two disagree — a peso price formatted with a
      // dollar sign, and a pack total of $4,779.03. Seen, not theorised.
      const country = this.dataset.country;
      const res = await fetch(
        `/collections/${encodeURIComponent(handle)}/products.json?limit=250` +
          (country ? `&country=${encodeURIComponent(country)}` : '')
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Typed as any[] deliberately: a third party's JSON, so the shape is
      // asserted by the mapping below rather than trusted up front.
      /** @type {any[]} */
      const raw = (await res.json())?.products ?? [];
      const kitLabel = this.dataset.kitLabel ?? 'Full CrewCup Kit';

      this.#designs = raw
        // Only products whose ONLY option is the tier belong in a 2-pack.
        //
        // Five products carry a second option (Colour) alongside Package. Their
        // variants are colour x package combinations, so listing one card per
        // variant asked the shopper to pick "BLUE / Full CrewCup Kit" from four
        // or more cards — a colour choice they were never told they were making,
        // in a step that is supposed to offer exactly two packages. One of them
        // also still carries junk option values ("Design Pattern 1/2/3").
        //
        // Excluding them keeps every card on this page meaning one thing. They
        // remain buyable on their own product pages, where the colour selector
        // exists and makes sense.
        .filter((p) => {
          const options = p.options ?? [];
          return options.length === 1 && options[0]?.name === 'Package';
        })
        .map((p) => ({
          handle: String(p.handle),
          title: String(p.title),
          image: p.images?.[0]?.src ?? null,
          // The whole gallery, so the media column can show thumbnails the way
          // the product page does rather than a single flat image.
          images: (p.images ?? []).map((/** @type {any} */ img) => String(img.src)).filter(Boolean),
          variants: (p.variants ?? []).map((/** @type {any} */ v) => ({
            id: String(v.id),
            title: String(v.title),
            price: String(v.price),
            // The variant's own shot, which the gallery prefers over the
            // product's when it exists — a kit variant photographs differently
            // from a bare cup.
            image: v.featured_image?.src ? String(v.featured_image.src) : null,
            // Kept so the package cards can show the same struck-through
            // was-price and Save % pill the product page's tiers show.
            compareAt: v.compare_at_price ? String(v.compare_at_price) : null,
            available: Boolean(v.available),
            isKit: String(v.title).includes(kitLabel),
          })),
        }))
        // A design with nothing buyable would render a card that cannot be picked.
        .filter((d) => d.variants.some((/** @type {Variant} */ v) => v.available));

      this.#loaded = true;
      if (state) state.textContent = '';
      this.#renderGrid();
    } catch (error) {
      if (state) state.textContent = 'Could not load the designs. Please reload the page and try again.';
      console.error('[two-pack] design list failed to load', error);
    }
  }

  /**
   * One design's charm and boot options.
   *
   * Fetched per design, and it has to be: the selector IDs match across designs
   * but the VALUES do not — `dinosaur` offers Unicorn and Monster Truck while
   * `flowers` offers T-Rex and Smiley Flower. Reusing one design's options for
   * all of them would submit a charm that design's bundle does not contain,
   * which Simple Bundles cannot match.
   *
   * @param {string} handle
   * @returns {Promise<Bundle | null>}
   */
  async #loadBundle(handle) {
    const cached = this.#bundles.get(handle);
    if (cached) return cached;

    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}?view=bundle-data`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Served as text/html (Liquid cannot set a content type), so parse the
      // body rather than trusting res.json().
      const bundle = JSON.parse(await res.text());
      this.#bundles.set(handle, bundle);
      return bundle;
    } catch (error) {
      console.error('[two-pack] bundle data failed to load', handle, error);
      return null;
    }
  }

  /* ------------------------------------------------------------ helpers */

  /**
   * @param {string | number} amount - A decimal amount, as products.json gives it.
   * @returns {string}
   */
  #money(amount) {
    const format = this.dataset.moneyFormat ?? '${{amount}}';
    const currency = this.dataset.currency ?? 'USD';
    // Both come from Liquid, so they follow the shopper's active market. Going
    // through the theme's own formatter keeps these prices identical to the
    // `| money` filter everywhere else — symbol, separators and precision.
    const minor = convertMoneyToMinorUnits(String(amount), currency);
    if (minor == null || !Number.isFinite(minor)) return '';
    return formatMoney(minor, format, currency);
  }

  /** @param {Variant[]} variants */
  #cheapestAvailable(variants) {
    return variants
      .filter((v) => v.available)
      .reduce(
        /** @param {Variant | null} low */
        (low, v) => (low == null || Number(v.price) < Number(low.price) ? v : low),
        null
      );
  }

  /** @param {number} cup */
  #designFor(cup) {
    const choice = this.#choice.get(cup);
    return this.#designs.find((d) => d.handle === choice?.handle) ?? null;
  }

  /**
   * The variant this cup resolves to right now.
   *
   * Null until a design is chosen, however early the package was picked — there
   * is no variant to name before then, and inventing one is how the wrong
   * product reaches a cart.
   *
   * @param {number} cup
   */
  #variantFor(cup) {
    const design = this.#designFor(cup);
    const tier = this.#choice.get(cup)?.tier;
    if (!design || !tier) return null;
    return design.variants.find((v) => v.isKit === (tier === 'full')) ?? null;
  }

  /**
   * A design to price the package cards against before one is chosen.
   *
   * The cards are a sales element and have to show real money from the first
   * paint, so they borrow the first eligible design's prices — the merchant's
   * configured reference if there is one, otherwise the collection's first
   * buyable design. Replaced by the real thing the moment a design is picked.
   */
  get #referenceDesign() {
    const configured = this.dataset.referenceHandle;
    return (
      (configured && this.#designs.find((d) => d.handle === configured)) ||
      this.#designs.find((d) => d.variants.some((v) => v.isKit && v.available)) ||
      this.#designs[0] ||
      null
    );
  }

  /**
   * The design whose prices the cards should show for this cup.
   * @param {number} cup
   */
  #displayDesign(cup) {
    return this.#designFor(cup) ?? this.#referenceDesign;
  }

  /** @param {number} cup */
  #bundleFor(cup) {
    // Keyed on the handle, which is null until a design is chosen — there is no
    // bundle to look up before then.
    const handle = this.#choice.get(cup)?.handle;
    return handle ? this.#bundles.get(handle) ?? null : null;
  }

  /** @param {number} cup */
  #isKit(cup) {
    return Boolean(this.#variantFor(cup)?.isKit);
  }

  /**
   * A kit is finished only when every selector its bundle offers has a value.
   * @param {number} cup
   */
  #isComplete(cup) {
    const choice = this.#choice.get(cup);
    // A design is required however early the package was chosen.
    if (!choice?.handle || !this.#variantFor(cup)) return false;
    if (!this.#isKit(cup)) return true;

    const bundle = this.#bundleFor(cup);
    if (!bundle?.configured) return false;
    return bundle.selectors.every((s) => Boolean(choice.addons.get(s.name)));
  }

  get #designsDone() {
    return this.#cups.every((c) => this.#designFor(c));
  }

  get #packagesDone() {
    return this.#cups.every((c) => this.#variantFor(c));
  }

  get #anyKit() {
    return this.#cups.some((c) => this.#isKit(c));
  }

  get #allDone() {
    return this.#cups.every((c) => this.#isComplete(c));
  }

  /** Which stages a shopper may jump to. Going back must never lose a choice. */
  /**
   * May this cup be opened yet?
   *
   * Cup 1 always; cup 2 once cup 1 is finished. Going back to cup 1 is always
   * allowed and never loses anything — state is keyed by cup, not by screen.
   * @param {number} cup
   */
  #tabEnabled(cup) {
    // Every earlier cup, not just the first: `cup === 1 || isComplete(1)` was
    // right for a pack of two, but in a 3-pack it opened cup 3 while cup 2 was
    // still empty — the dead end this rule exists to prevent. Once the pack is
    // finished every tab is reachable again, because all its predecessors are
    // complete.
    return this.#cups.filter((c) => c < cup).every((c) => this.#isComplete(c));
  }

  /**
   * Readable heading for an option group.
   *
   * The app's labels are whatever the metafield gives us — "Choose Your Charm"
   * is clear, but "Color" alone is meaningless next to a charm. Qualify it from
   * the component's own product title ("Protective Boot …" -> "Boot Color")
   * rather than hardcoding a map, which would mislabel the day a third
   * component is added.
   *
   * @param {BundleSelector} selector
   */
  #groupHeading(selector) {
    const label = (selector.label ?? '').trim();
    const qualifier = (selector.product_title ?? '').match(QUALIFIERS)?.[1];
    const qualified =
      qualifier && !new RegExp(`\\b${qualifier}\\b`, 'i').test(label) ? `${qualifier} ${label}` : label;
    return /^choose/i.test(qualified) ? qualified : `Choose a ${qualified}`;
  }

  /**
   * A short noun for one option group, for use mid-sentence.
   *
   * The app's labels do not fit an instruction: "Choose Your Charm" would give
   * "Choose a choose your charm for Cup 1 to continue." Strip the leading verb
   * and qualify a bare label the same way the heading does, so the sentence
   * reads "Choose a charm for Cup 1" and "Choose a boot color for Cup 2".
   *
   * @param {BundleSelector} selector
   */
  #optionNoun(selector) {
    const label = (selector.label ?? '').trim();
    const qualifier = (selector.product_title ?? '').match(QUALIFIERS)?.[1];
    const qualified =
      qualifier && !new RegExp(`\\b${qualifier}\\b`, 'i').test(label) ? `${qualifier} ${label}` : label;
    return qualified.replace(/^choose\s+(your|a|an)?\s*/i, '').trim().toLowerCase();
  }

  /**
   * Is this cup the one being worked on, or one already settled?
   *
   * Below 750px a finished cup collapses to its summary so the cup in hand is
   * the only full card on screen, while nothing already decided disappears.
   *
   * @param {number} cup
   * @returns {'expanded' | 'summary'}
   */
  #modeFor(cup) {
    return this.#isComplete(cup) && cup !== this.#tab ? 'summary' : 'expanded';
  }

  get #reducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  /**
   * Tell assistive tech what changed. Step changes and validation are otherwise
   * only visible, which makes the guided flow unusable without sight.
   * @param {string} message
   */
  #announce(message) {
    const region = this.querySelector('[data-announce]');
    if (region instanceof HTMLElement && message && region.textContent !== message) {
      region.textContent = message;
    }
  }

  /**
   * Bring something into view, honouring reduced motion.
   *
   * Only called when a cup has just been FINISHED, never on every change — a
   * page that scrolls while someone is still choosing moves the target out from
   * under their thumb.
   *
   * @param {Element | null | undefined} target
   */
  #reveal(target) {
    if (!target) return;
    target.scrollIntoView({ block: 'nearest', behavior: this.#reducedMotion ? 'auto' : 'smooth' });
  }

  /**
   * Bring the next unanswered thing into view.
   *
   * Only ever called once something has just been FINISHED, so it cannot move
   * the page while a choice is still being made.
   *
   * @param {number} cup
   */
  #revealNext(cup) {
    // Scrolls WITHIN the cup being configured, and never changes tab.
    //
    // It used to hand over to the other cup as soon as this one was complete,
    // which was right when a Continue button drove the flow. It is wrong now
    // that each cup ends with "Save Cup 1 & Choose Cup 2": picking Single
    // completes a cup instantly, so the page jumped to cup 2 the moment the
    // package was chosen — before the shopper had seen their own cup, and
    // taking the gallery with it. Moving between cups is the shopper's decision
    // now, made with that button or the tabs.
    if (this.#isComplete(cup)) return;
    this.#reveal(this.#variantFor(cup) ? this.#slot('kit', cup) : this.#slot('package', cup));
  }

  /**
   * One scroll chevron for a rail, using the theme's own icon asset.
   *
   * The product page's rails have these, and matching it was the explicit ask.
   * They stay hidden unless the row actually overflows, so a component with
   * three options shows no chrome — same rule as blocks/_kit-component.liquid.
   *
   * @param {string} direction
   * @param {string} ariaLabel
   */
  #railNav(direction, ariaLabel) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `kit-rail__nav kit-rail__nav--${direction === '1' ? 'next' : 'prev'}`;
    button.dataset.kitScroll = direction;
    button.setAttribute('aria-label', ariaLabel);
    button.hidden = true;

    const template = this.querySelector(direction === '1' ? '[data-icon-next]' : '[data-icon-prev]');
    if (template instanceof HTMLTemplateElement) button.append(template.content.cloneNode(true));
    return button;
  }

  /** Show a rail's chevrons only where it overflows, and only where there is more to see. */
  #syncRails = () => {
    for (const rail of this.querySelectorAll('[data-kit-rail]')) {
      const cards = rail.querySelector('.kit-cards');
      if (!(cards instanceof HTMLElement)) continue;

      const overflows = cards.scrollWidth - cards.clientWidth > 1;
      const atStart = cards.scrollLeft <= 1;
      const atEnd = cards.scrollLeft >= cards.scrollWidth - cards.clientWidth - 1;

      for (const nav of rail.querySelectorAll('[data-kit-scroll]')) {
        if (!(nav instanceof HTMLElement)) continue;
        const forward = nav.dataset.kitScroll === '1';
        nav.hidden = !overflows || (forward ? atEnd : atStart);
      }

      if (!cards.dataset.railBound) {
        cards.dataset.railBound = 'true';
        cards.addEventListener('scroll', this.#syncRails, { passive: true });
      }
    }
  };

  /** @param {string} className */
  #tick(className) {
    const tick = document.createElement('span');
    tick.className = className;
    tick.setAttribute('aria-hidden', 'true');
    const template = this.querySelector('[data-icon-tick]');
    if (template instanceof HTMLTemplateElement) tick.append(template.content.cloneNode(true));
    return tick;
  }

  get #cupWord() {
    return this.dataset.cupWord || 'Cup';
  }

  /**
   * The one action the sticky bar offers, and what it does.
   *
   * It always names the next thing THIS cup needs, then moves to the other cup,
   * and only becomes Add to Cart when the pack is complete. A primary button
   * that sits permanently disabled teaches a shopper that the main control on
   * the page does not work.
   *
   * @returns {{ label: string, act: () => void, enabled: boolean }}
   */
  #primaryAction() {
    const word = this.#cupWord;
    const cup = this.#tab;

    if (!this.#designFor(cup)) {
      return { label: `Choose ${word} ${cup} Design`, act: () => this.#openPicker(cup), enabled: true };
    }

    if (!this.#variantFor(cup)) {
      return {
        label: `Choose ${word} ${cup} Package`,
        act: () => this.#reveal(this.#slot('package', cup)),
        enabled: true,
      };
    }

    if (!this.#isComplete(cup)) {
      const bundle = this.#bundleFor(cup);
      if (!bundle?.configured) {
        return {
          label: `Change ${word} ${cup} Package`,
          act: () => this.#reveal(this.#slot('package', cup)),
          enabled: true,
        };
      }
      const choice = this.#choice.get(cup);
      const missing = bundle.selectors.find((sel) => !choice?.addons.get(sel.name));
      if (missing) {
        return {
          label: `Choose ${word} ${cup} ${this.#optionNoun(missing)}`,
          act: () => this.#reveal(this.#slot('kit', cup)),
          enabled: true,
        };
      }
    }

    const other = this.#nextUnfinished(cup);
    if (other != null) {
      return { label: `Choose ${word} ${other}`, act: () => this.#goTab(other), enabled: true };
    }

    return {
      label: this.#packText(this.dataset.addLabel || 'Add [n]-Pack to Cart'),
      act: () => this.#addToCart(),
      enabled: this.#allDone && !this.#busy,
    };
  }

  /* --------------------------------------------------------------- render */

  #render() {
    this.dataset.cup = String(this.#tab);

    for (const panel of this.querySelectorAll('[data-cup-panel]')) {
      if (panel instanceof HTMLElement) panel.hidden = Number(panel.dataset.cupPanel) !== this.#tab;
    }

    for (const cup of this.#cups) {
      this.#renderDesignSlot(cup);
      this.#renderPackageSlot(cup);
      this.#renderKitSlot(cup);
      this.#renderReviewSlot(cup);
    }

    for (const name of ['design', 'package', 'kit', 'review']) {
      for (const cup of this.#cups) {
        const slot = this.#slot(name, cup);
        if (slot instanceof HTMLElement) slot.dataset.mode = this.#modeFor(cup);
      }
    }

    this.#renderMedia();
    this.#renderPackText();
    this.#renderPackDeal();
    this.#renderTabs();
    this.#renderBar();
    this.#syncRails();
  }

  /** The cup tabs, their state, and the nudge onward. */
  #renderTabs() {
    // Panels hide themselves — the sync above hides everything that is not the
    // active tab — but a tab for a cup outside the pack would stay on screen.
    for (const tab of this.querySelectorAll('[data-cup-tab]')) {
      if (tab instanceof HTMLElement) tab.hidden = Number(tab.dataset.cupTab) > this.#packSize;
    }

    // The size buttons show which pack is being built.
    for (const button of this.querySelectorAll('[data-pack-size]')) {
      if (!(button instanceof HTMLElement)) continue;
      const size = Number(button.dataset.packSize);
      button.setAttribute('aria-pressed', String(size === this.#packSize));
    }

    for (const cup of this.#cups) {
      const tab = this.querySelector(`[data-cup-tab="${cup}"]`);
      const state = this.querySelector(`[data-cup-state="${cup}"]`);
      const complete = this.#isComplete(cup);
      const enabled = this.#tabEnabled(cup);

      if (tab instanceof HTMLButtonElement) {
        tab.setAttribute('aria-selected', String(cup === this.#tab));
        // Disabled rather than hidden: the shape of the pack stays visible, and
        // a tab leading to a half-finished first cup cannot be reached by
        // keyboard either.
        tab.disabled = !enabled;
        tab.dataset.done = String(complete);
      }

      if (state instanceof HTMLElement) {
        const design = this.#designFor(cup);
        const variant = this.#variantFor(cup);
        state.textContent = !enabled
          ? `Finish ${this.#cupWord} 1 first`
          : !design
            ? 'Choose a design'
            : !variant
              ? 'Choose a package'
              : complete
                ? design.title
                : 'Choose your add-ons';
      }

      // The cup's own completion action, at the end of its configuration.
      const done = this.querySelector(`[data-cup-done="${cup}"]`);
      if (done instanceof HTMLButtonElement) {
        const other = this.#nextUnfinished(cup);
        done.textContent =
          other == null
            ? this.#packText('Review Your [n]-Pack')
            : `Save ${this.#cupWord} ${cup} & Choose ${this.#cupWord} ${other}`;
        // Enabled only on a genuinely complete cup: a design, a package, and —
        // for a kit — a charm and a boot.
        done.disabled = !complete;
      }

      const nudge = this.querySelector(`[data-cup-nudge="${cup}"]`);
      if (nudge instanceof HTMLElement) {
        const other = this.#nextUnfinished(cup);
        const show = cup === this.#tab && complete && other != null;
        nudge.hidden = !show;
        if (show) nudge.textContent = `${this.#cupWord} ${cup} is all set — now choose ${this.#cupWord} ${other}.`;
      }
    }

    const review = this.querySelector('[data-review]');
    if (review instanceof HTMLElement) review.hidden = !this.#allDone;

    this.#announce(
      this.#allDone ? this.#packText(this.dataset.readyText || 'Your [pack] is ready.') : `${this.#cupWord} ${this.#tab}. ${this.#firstMissing()}`
    );
  }

  /**
   * Finish a cup and hand over to the other.
   *
   * Nothing is "saved" in the sense of being written anywhere — the state was
   * already correct — but the shopper has said they are done with this cup, and
   * the page should behave as though a step closed: tick the tab, unlock and
   * move to the other cup, and say so.
   *
   * @param {number} cup
   */
  #saveCup(cup) {
    if (!this.#isComplete(cup)) return;

    const other = this.#nextUnfinished(cup);
    if (other == null) {
      // Both done — the review summary is what comes next.
      this.#render();
      this.#reveal(this.querySelector('[data-review]'));
      return;
    }

    this.#toast(`${this.#cupWord} ${cup} is ready — now build ${this.#cupWord} ${other}.`);
    this.#goTab(other);
  }

  /**
   * A brief confirmation. Announced as well as shown, and cleared on a timer so
   * it cannot linger next to a later, unrelated choice.
   * @param {string} message
   */
  #toast(message) {
    const toast = this.querySelector('[data-toast]');
    if (!(toast instanceof HTMLElement)) return;

    toast.textContent = message;
    toast.hidden = false;

    if (this.#toastTimer) clearTimeout(this.#toastTimer);
    this.#toastTimer = setTimeout(() => {
      toast.hidden = true;
      toast.textContent = '';
    }, 6000);
  }

  /** @param {number} cup */
  #goTab(cup) {
    if (!this.#tabEnabled(cup)) return;
    this.#tab = cup;
    this.#render();
    this.#reveal(this.querySelector(`[data-cup-panel="${cup}"]`));
    const tab = this.querySelector(`[data-cup-tab="${cup}"]`);
    if (tab instanceof HTMLElement) tab.focus({ preventScroll: true });
  }

  /**
   * The media column: the ACTIVE cup's product image.
   *
   * Priority, and the order matters — a kit variant photographs differently from
   * a bare cup, so the variant's own shot wins where it exists:
   *   1. the selected package variant's featured image
   *   2. the selected design's featured image
   *   3. the promotional builder image
   *
   * Keyed on the active cup, so switching tabs can never show the other cup's
   * product. Before any design is chosen this is the promotional image rather
   * than a large empty panel with a message in the middle of it.
   */
  #renderMedia() {
    const gallery = this.querySelector('[data-gallery]');
    if (!(gallery instanceof HTMLElement)) return;

    const cup = this.#tab;
    const design = this.#designFor(cup);
    const variant = this.#variantFor(cup);
    const promo = this.dataset.promoImage || null;

    const chosen = this.#activeImage.get(cup);
    const src = chosen ?? variant?.image ?? design?.image ?? promo;
    // A video may open the page with no image set at all, so an empty `src` is
    // only empty when there is no opening video to fall back on either.
    const openingVideo = !design ? this.dataset.promoVideo || '' : '';
    if (!src && !openingVideo) {
      gallery.replaceChildren();
      return;
    }

    const main = document.createElement('div');
    main.className = 'gallery__main';

    // AN UPLOADED VIDEO CAN OPEN THE PAGE IN PLACE OF THE IMAGE.
    //
    // Only in the opening state: once a design is chosen this panel is that
    // product's own photography and a promotional clip would be misleading.
    //
    // Muted, looping and inline, with no controls, because the whole panel is
    // the "choose a design" button — a control bar inside a button would give
    // two targets in one place and swallow the tap that starts the flow. Where
    // motion is unwelcome the poster frame is shown as a still instead.
    const promoVideo = this.dataset.promoVideo || '';
    const still = !matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!design && promoVideo && still) {
      const video = document.createElement('video');
      video.className = 'gallery__video';
      video.src = promoVideo;
      if (this.dataset.promoPoster) video.poster = this.dataset.promoPoster;
      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.tabIndex = -1;
      // Autoplay is refused unless the attribute is present at parse time as
      // well as the property being set, and iOS additionally requires the
      // literal playsinline attribute.
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      main.append(video);
    } else {
      // With a video uploaded but motion unwelcome, its poster frame stands in —
      // and that poster may be the only source there is, if no image was set.
      const stillSrc = (!design && promoVideo && !still ? this.dataset.promoPoster : '') || src || '';
      if (stillSrc) {
        const img = document.createElement('img');
        img.className = 'gallery__img';
        img.width = 900;
        img.height = 900;
        img.loading = 'eager';
        img.decoding = 'async';
        // Empty alt where the design name sits right beside it; the promotional
        // image keeps the alt text the merchant wrote for it.
        img.alt = design ? '' : this.dataset.promoAlt || '';
        img.src = stillSrc;
        main.append(img);
      }
    }

    const parts = [main];

    if (design && design.images.length > 1) {
      const thumbs = document.createElement('div');
      thumbs.className = 'gallery__thumbs';
      for (const thumbSrc of design.images.slice(0, 8)) {
        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'gallery__thumb';
        thumb.dataset.galleryThumb = thumbSrc;
        thumb.setAttribute('aria-pressed', String(thumbSrc === src));
        thumb.setAttribute('aria-label', 'Show this image');
        const t = document.createElement('img');
        t.src = thumbSrc;
        t.loading = 'lazy';
        t.width = 120;
        t.height = 120;
        t.alt = '';
        thumb.append(t);
        thumbs.append(thumb);
      }
      parts.push(thumbs);
    }

    // THE WHOLE IMAGE IS THE ACTION.
    //
    // A small dark button under a large photograph is not an obvious "start
    // here" — it reads as a caption. Before a design is chosen the entire media
    // area is one button carrying a large call to action across the bottom of
    // the image, so the biggest thing on the page is also the thing to tap.
    // One element, so a screen reader hears one control with one name rather
    // than an image followed by a mystery button.
    if (!design) {
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'gallery__trigger';
      trigger.dataset.openPicker = String(cup);

      const cta = document.createElement('span');
      cta.className = 'gallery__cta';
      cta.textContent = `Choose ${this.#cupWord} ${cup} Design`;

      const hint = document.createElement('span');
      hint.className = 'gallery__hint';
      hint.textContent = this.dataset.chooseHelp || 'Pick any 20oz CrewCup design';

      const overlay = document.createElement('span');
      overlay.className = 'gallery__overlay';
      overlay.append(cta, hint);

      trigger.append(main, overlay);
      gallery.replaceChildren(trigger);
      return;
    }

    // Once a design is chosen the image stays tappable to change it, with the
    // name beside a plain "Change design" for anyone who reads rather than taps.
    const actions = document.createElement('div');
    actions.className = 'gallery__actions';

    const name = document.createElement('p');
    name.className = 'gallery__name';
    name.textContent = design.title;

    const change = document.createElement('button');
    change.type = 'button';
    change.className = 'gallery__change';
    change.dataset.openPicker = String(cup);
    change.textContent = this.dataset.changeLabel || 'Change design';

    actions.append(name, change);
    parts.push(actions);
    gallery.replaceChildren(...parts);
  }

  /**
   * The compact row a finished cup collapses to on mobile.
   *
   * Shown instead of the full controls once that cup's decision for this stage
   * is made, so a phone carries one open card at a time without hiding what has
   * already been decided.
   *
   * @param {number} cup
   * @param {Design} design
   * @param {string} detail - What was chosen, in a few words.
   */
  #summaryRow(cup, design, detail) {
    const row = document.createElement('div');
    row.className = 'sum';

    const img = document.createElement('img');
    img.className = 'sum__img';
    img.width = 56;
    img.height = 56;
    img.loading = 'lazy';
    // Empty alt on purpose: the design name sits immediately beside it, and a
    // duplicate would have a screen reader read every product twice.
    img.alt = '';
    if (design.image) img.src = design.image;

    const text = document.createElement('div');
    text.className = 'sum__text';

    const name = document.createElement('p');
    name.className = 'sum__name';
    name.textContent = `${this.#cupWord} ${cup}: ${design.title}`;

    const meta = document.createElement('p');
    meta.className = 'sum__meta';
    meta.append(this.#tick('sum__tick'), document.createTextNode(detail));

    text.append(name, meta);
    row.append(img, text);
    return row;
  }

  /**
   * @param {HTMLElement[]} body
   * @param {HTMLElement | null} summary
   */
  #card(body, summary) {
    const card = document.createElement('div');
    card.className = 'card';
    if (summary) card.append(summary);

    const inner = document.createElement('div');
    inner.className = 'card__body';
    inner.append(...body);
    card.append(inner);
    return card;
  }

  /**
   * The design status, in plain words.
   *
   * It used to read "Cup design: Not chosen — Choose a design", which said the
   * same thing as the picture above it and the tab beside it, and led with a
   * negative about something the shopper had not reached yet.
   *
   * Now it POINTS AT the picture rather than competing with it: it names what is
   * missing in ordinary language and tells you where to do it, with a real
   * button for anyone who would rather press one than tap a photograph. Once a
   * design is chosen it simply confirms it, with a tick.
   *
   * @param {number} cup
   */
  #renderDesignSlot(cup) {
    const slot = this.#slot('design', cup);
    if (!slot) return;

    const design = this.#designFor(cup);

    const row = document.createElement('div');
    row.className = design ? 'designrow designrow--done' : 'designrow';

    const text = document.createElement('div');
    text.className = 'designrow__text';

    const headline = document.createElement('p');
    headline.className = 'designrow__headline';

    if (design) {
      headline.append(this.#tick('designrow__tick'));
      headline.append(document.createTextNode(design.title));
    } else {
      headline.textContent = this.dataset.designNone || 'No design picked yet';
    }

    text.append(headline);

    if (!design) {
      const hint = document.createElement('p');
      hint.className = 'designrow__hint';
      // Teaches the primary action instead of duplicating it.
      hint.textContent = this.dataset.designHint || 'Tap the picture above to pick one';
      text.append(hint);
    }

    const action = document.createElement('button');
    action.type = 'button';
    action.className = 'designrow__btn';
    action.dataset.openPicker = String(cup);
    action.textContent = design
      ? this.dataset.changeLabel || 'Change design'
      : this.dataset.designPick || 'Pick a design';

    row.append(text, action);
    slot.replaceChildren(row);
  }


  /** The compact "design selected" row, shared by stages 1 and 2. */

  /** STAGE 2 — a package for this cup. */
  /** @param {number} cup */
  #renderPackageSlot(cup) {
    const slot = this.#slot('package', cup);
    if (!slot) return;

    // Priced against the chosen design, or a reference one before then. The
    // cards are the page's main sales element and have to show real money from
    // the first paint rather than waiting behind a design choice — and the
    // prices come from product data either way, never a hardcoded figure.
    const design = this.#displayDesign(cup);
    if (!design) {
      slot.replaceChildren();
      return;
    }

    const choice = this.#choice.get(cup);
    const bundle = this.#bundleFor(cup);
    const singleLabel = this.dataset.singleLabel || 'Cup Only';
    const singleBlurb = this.dataset.singleBlurb ?? '';
    const kitBlurb = this.dataset.kitBlurb ?? '';
    const popular = this.dataset.popularLabel ?? '';

    const head = document.createElement('div');
    head.className = 'card__head';
    const label = document.createElement('span');
    label.className = 'card__cup';
    label.textContent = `${this.#cupWord} ${cup}`;
    head.append(label);

    const group = document.createElement('fieldset');
    group.className = 'pkg';

    const legend = document.createElement('legend');
    legend.className = 'grp__legend';
    // Naming the cup in the legend is what stops two identical option sets on
    // one screen being confused for each other.
    legend.textContent = `${this.#cupWord} ${cup} package`;
    group.append(legend);

    // `.kit-tiers` is the product page's own flex column — it carries the gap
    // between the two tier cards. On the product page it IS the fieldset; here
    // the fieldset is `.pkg` (it holds the per-cup legend), so the class has to
    // land on this inner wrapper or the cards render flush against each other.
    const list = document.createElement('div');
    list.className = 'kit-tiers';

    // KIT FIRST, then the single — the order the product page uses
    // (variant-option-kit-tiers.liquid emits upgrade_cards before base_cards),
    // and the order that leads with what the shop wants to sell.
    const ordered = [...design.variants].sort((x, y) => Number(y.price) - Number(x.price));

    for (const variant of ordered) {
      // A design can carry the Package option while its bundle has not been
      // registered in Simple Bundles yet. Its product page already blocks the
      // kit tier; without the same guard the builder would sell a kit with no
      // components.
      const kitUnavailable = variant.isKit && bundle != null && bundle.configured === false;

      // THE PRODUCT PAGE'S TIER CARD, element for element.
      // snippets/variant-option-kit-tiers.liquid builds
      //   label.kit-tier > input.kit-tier__input + span.kit-tier__card
      // and its CSS keys off that adjacency, so the structure is not cosmetic —
      // a wrapper in the wrong place silently kills the selected state. The
      // styles come from snippets/two-pack-kit-visuals.liquid, extracted from
      // that same file so the two cannot drift.
      const label = document.createElement('label');
      label.className = 'kit-tier';
      label.dataset.tier = variant.isKit ? 'full' : 'single';

      const input = document.createElement('input');
      input.className = 'kit-tier__input';
      input.type = 'radio';
      // Scoped per cup AND per section, so two cups never share a radio group.
      input.name = `${this.dataset.uid}-cup-${cup}-package`;
      input.value = variant.isKit ? 'full' : 'single';
      input.dataset.tierInput = '';
      input.dataset.tier = variant.isKit ? 'full' : 'single';
      input.checked = choice?.tier === (variant.isKit ? 'full' : 'single');
      input.disabled = !variant.available || kitUnavailable;
      input.setAttribute(
        'aria-label',
        `${variant.isKit ? variant.title : singleLabel} — ${this.#money(variant.price)}`
      );

      const card = document.createElement('span');
      card.className = 'kit-tier__card';

      const wasValue = Number(variant.compareAt);
      const nowValue = Number(variant.price);
      const hasDiscount = Number.isFinite(wasValue) && wasValue > nowValue;
      const savePct = hasDiscount ? Math.round(((wasValue - nowValue) / wasValue) * 100) : 0;

      // The flag overlapping the top edge: "Most popular · Save 31%".
      if (variant.isKit && this.dataset.badgeText && !kitUnavailable) {
        const flag = document.createElement('span');
        flag.className = 'kit-tier__flag';
        flag.textContent = hasDiscount
          ? `${this.dataset.badgeText} · Save ${savePct}%`
          : this.dataset.badgeText;
        card.append(flag);
      }

      const head = document.createElement('span');
      head.className = 'kit-tier__head';

      const radio = document.createElement('span');
      radio.className = 'kit-tier__radio';
      radio.setAttribute('aria-hidden', 'true');

      const titles = document.createElement('span');
      titles.className = 'kit-tier__titles';

      const title = document.createElement('span');
      title.className = 'kit-tier__title';
      title.textContent = variant.isKit ? variant.title : singleLabel;
      titles.append(title);

      const subtitleText = variant.isKit ? kitBlurb : singleBlurb;
      if (subtitleText) {
        const subtitle = document.createElement('span');
        subtitle.className = 'kit-tier__subtitle';
        subtitle.textContent = subtitleText;
        titles.append(subtitle);
      }

      const price = document.createElement('span');
      price.className = 'kit-tier__price';
      const now = document.createElement('span');
      now.className = 'kit-tier__price-now';
      now.textContent = this.#money(variant.price);
      price.append(now);

      // Only a genuine reduction is shown as one: a compare-at at or below the
      // price is not a discount, and claiming one by accident is not a mistake a
      // shop should make.
      if (hasDiscount) {
        const was = document.createElement('s');
        was.className = 'kit-tier__price-was';
        was.textContent = this.#money(variant.compareAt ?? '');
        price.append(was);
      }

      head.append(radio, titles, price);
      card.append(head);

      if (variant.isKit) {
        const pills = document.createElement('span');
        pills.className = 'kit-tier__pills';
        if (hasDiscount) {
          const save = document.createElement('span');
          save.className = 'kit-tier__pill kit-tier__pill--save';
          save.textContent = `Save ${savePct}%`;
          pills.append(save);
        }
        if (this.dataset.shipPill) {
          const ship = document.createElement('span');
          ship.className = 'kit-tier__pill kit-tier__pill--ship';
          ship.textContent = this.dataset.shipPill;
          pills.append(ship);
        }
        if (pills.childElementCount) card.append(pills);
      }

      // Bullets. The product page's CSS reveals these only on the CHECKED tier,
      // so both cards carry them and only one set is ever visible.
      const bulletSource = variant.isKit ? this.dataset.kitBullets : this.dataset.singleBullets;
      const lines = (bulletSource ?? '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

      if (lines.length) {
        const bullets = document.createElement('span');
        bullets.className = 'kit-tier__bullets';
        for (const line of lines) {
          const bullet = document.createElement('span');
          bullet.className = 'kit-tier__bullet';
          const text = document.createElement('span');
          text.className = 'kit-tier__bullet-text';
          text.textContent = line;
          bullet.append(this.#tick('kit-tier__check'), text);
          bullets.append(bullet);
        }
        card.append(bullets);
      }

      if (variant.isKit && hasDiscount) {
        const saving = document.createElement('span');
        saving.className = 'kit-tier__saving';
        saving.textContent = `You save ${this.#money((wasValue - nowValue).toFixed(2))}`;
        card.append(saving);
      }

      // Say why an option cannot be taken. A silently disabled card reads as a
      // broken page.
      if (!variant.available || kitUnavailable) {
        const why = document.createElement('span');
        why.className = 'kit-tier__saving';
        why.textContent = !variant.available ? 'Sold out' : 'Kit not available for this design yet';
        card.append(why);
      }

      label.append(input, card);
      list.append(label);
    }

    group.append(list);

    const chosenVariant = this.#variantFor(cup);
    const detail = chosenVariant
      ? chosenVariant.isKit
        ? chosenVariant.title
        : this.dataset.singleLabel || 'Cup Only'
      : '';

    // No design card in here. `design` may be the REFERENCE product — the one
    // this cup borrows prices from before a design is chosen — and rendering it
    // as a card announced "Flowers Spill Proof Kids CREWCUP 20oz — Design
    // selected" directly beneath a row reading "Cup design: Not chosen". The
    // design is stated once by the status row and shown once by the gallery.
    const realDesign = this.#designFor(cup);
    slot.replaceChildren(
      this.#card([head, group], chosenVariant && realDesign ? this.#summaryRow(cup, realDesign, detail) : null)
    );
  }

  /** STAGE 3 — charm and boot, kits only. */
  /** @param {number} cup */
  #renderKitSlot(cup) {
    const slot = this.#slot('kit', cup);
    if (!slot) return;

    // Deliberately the REAL design, not the reference one: charm and boot lists
    // are per design, so a reference product's options must never be offered.
    const design = this.#designFor(cup);
    const bundle = this.#bundleFor(cup);
    const choice = this.#choice.get(cup);

    if (!(slot instanceof HTMLElement)) return;

    // A single never gets charm or boot controls — nor, therefore, charm or
    // boot properties.
    if (choice?.tier !== 'full') {
      slot.replaceChildren();
      return;
    }

    // Kit chosen but no design yet: keep the section visible so personalisation
    // reads as part of the offer, but disabled. The charm lists differ per
    // design, so collecting a value now could record one the chosen design does
    // not contain — the exact mismatch that makes an order unfulfillable.
    if (!design) {
      const waiting = document.createElement('div');
      waiting.className = 'kit-customizer kit-customizer--waiting';

      if (this.dataset.stepEyebrow) {
        const eyebrow = document.createElement('span');
        eyebrow.className = 'kit-customizer__step';
        eyebrow.textContent = this.dataset.stepEyebrow;
        waiting.append(eyebrow);
      }

      const title = document.createElement('span');
      title.className = 'kit-customizer__row-title';
      title.textContent = this.dataset.customizeTitle || 'Customize your kit';

      const note = document.createElement('p');
      note.className = 'kit-customizer__notice';
      note.textContent = 'Choose your cup design to personalize your kit.';

      waiting.append(title, note);
      slot.replaceChildren(waiting);
      return;
    }


    // A persistent identifier, so which cup is being personalised is never in
    // doubt — two kits side by side otherwise look like one duplicated form.
    const head = document.createElement('div');
    head.className = 'card__head card__head--kit';

    const label = document.createElement('span');
    label.className = 'card__cup';
    label.textContent = `Personalising ${this.#cupWord} ${cup}`;

    const who = document.createElement('span');
    who.className = 'card__design';
    who.textContent = design.title;

    const headText = document.createElement('span');
    headText.className = 'card__headtext';
    headText.append(label, who);

    const headImg = document.createElement('img');
    headImg.className = 'card__thumb';
    headImg.width = 48;
    headImg.height = 48;
    headImg.loading = 'lazy';
    headImg.alt = '';
    if (design.image) headImg.src = design.image;

    head.append(headImg, headText);

    /** @type {HTMLElement[]} */
    const body = [head];

    if (!bundle?.configured) {
      const note = document.createElement('p');
      note.className = 'note';
      note.textContent = 'This design is not set up as a kit yet, so it can only be added on its own.';
      body.push(note);
      slot.replaceChildren(this.#card(body, null));
      return;
    }

    // THE PRODUCT PAGE'S "WHAT'S INCLUDED" PANEL, element for element:
    // an eyebrow, the title with a live summary of the choices, then a bordered
    // panel of rails. blocks/product-kit-customizer.liquid builds exactly this,
    // and its styles come through snippets/two-pack-kit-visuals.liquid.
    const customizer = document.createElement('div');
    customizer.className = 'kit-customizer kit-customizer--bundle';

    if (this.dataset.stepEyebrow) {
      const eyebrow = document.createElement('span');
      eyebrow.className = 'kit-customizer__step';
      eyebrow.textContent = this.dataset.stepEyebrow;
      customizer.append(eyebrow);
    }

    const chead = document.createElement('div');
    chead.className = 'kit-customizer__head';

    const ctitle = document.createElement('span');
    ctitle.className = 'kit-customizer__row-title';
    // Cup number in the title, because two kits on one page are otherwise two
    // identical panels.
    ctitle.textContent = `${this.dataset.customizeTitle || 'Customize your kit'} — ${this.#cupWord} ${cup}`;

    const csummary = document.createElement('span');
    csummary.className = 'kit-customizer__summary';
    csummary.setAttribute('aria-live', 'polite');
    csummary.dataset.kitSummary = String(cup);

    chead.append(ctitle, csummary);
    customizer.append(chead);

    const panel = document.createElement('div');
    panel.className = 'kit-customizer__panel';

    if (this.dataset.customizeSub) {
      const sub = document.createElement('p');
      sub.className = 'kit-customizer__subtitle';
      sub.textContent = this.dataset.customizeSub;
      panel.append(sub);
    }

    bundle.selectors.forEach((selector, index) => {
      // Nothing is preselected — see the note in #isComplete. The panel reads
      // the stored value and never writes one, so a kit stays incomplete until a
      // real choice is made.
      const current = choice?.addons.get(selector.name) ?? '';

      const row = document.createElement('div');
      row.className = 'kit-component';

      const rowBody = document.createElement('div');
      rowBody.className = 'kit-component__body';

      const rowHead = document.createElement('div');
      rowHead.className = 'kit-component__head';

      const legend = document.createElement('span');
      legend.className = 'kit-component__label';
      legend.textContent = this.#groupHeading(selector);
      rowHead.append(legend);

      // The component's own product name, muted on the right — the product page
      // shows it so a shopper knows which item they are configuring. Truncated
      // to 44 as it is there.
      const sourceTitle = (selector.product_title ?? '').trim();
      if (sourceTitle) {
        const source = document.createElement('span');
        source.className = 'kit-component__note';
        source.textContent = sourceTitle.length > 44 ? `${sourceTitle.slice(0, 44)}…` : sourceTitle;
        rowHead.append(source);
      }

      rowBody.append(rowHead);

      const rail = document.createElement('div');
      rail.className = 'kit-rail';
      rail.dataset.kitRail = '';

      rail.append(this.#railNav('-1', `Show previous ${this.#optionNoun(selector)} options`));

      const cards = document.createElement('div');
      cards.className = 'kit-cards';
      cards.setAttribute('role', 'radiogroup');
      cards.setAttribute('aria-label', `${this.#cupWord} ${cup}: ${this.#groupHeading(selector)}`);

      for (const value of selector.values) {
        // A real radio inside the card, not a button with aria-pressed: the
        // product page uses buttons writing to a hidden <select>, but there is
        // no form here, and a radio group is the honest control for "choose one".
        const card = document.createElement('label');
        card.className = 'kit-card';

        const input = document.createElement('input');
        input.className = 'kit-card__input';
        input.type = 'radio';
        input.name = `${this.dataset.uid}-cup-${cup}-addon-${index}`;
        input.value = value.value;
        input.dataset.addonSelector = selector.name;
        input.checked = value.value === current;

        const media = document.createElement('span');
        media.className = 'kit-card__media';
        if (value.image) {
          const img = document.createElement('img');
          img.className = 'kit-card__img';
          img.src = value.image;
          img.loading = 'lazy';
          img.width = 120;
          img.height = 120;
          img.alt = '';
          media.append(img);
        }

        const name = document.createElement('span');
        name.className = 'kit-card__label';
        name.textContent = value.value;

        card.append(input, media, name);
        cards.append(card);
      }

      rail.append(cards, this.#railNav('1', `Show more ${this.#optionNoun(selector)} options`));
      rowBody.append(rail);
      row.append(rowBody);
      panel.append(row);
    });

    customizer.append(panel);
    body.push(customizer);

    // "Horse · Light Pink" beside the title, as the product page shows it.
    const chosenValues = bundle.selectors
      .map((sel) => choice?.addons.get(sel.name))
      .filter(Boolean);
    csummary.textContent = chosenValues.join(' · ');

    const choiceNow = this.#choice.get(cup);
    const detail = bundle.selectors.map((sel) => choiceNow?.addons.get(sel.name)).filter(Boolean).join(', ');

    slot.replaceChildren(
      this.#card(body, this.#isComplete(cup) ? this.#summaryRow(cup, design, detail) : null)
    );
  }

  /** STAGE 4 — review. */
  /** @param {number} cup */
  #renderReviewSlot(cup) {
    const slot = this.#slot('review', cup);
    if (!slot) return;

    const design = this.#designFor(cup);
    const variant = this.#variantFor(cup);
    if (!design || !variant) {
      slot.replaceChildren();
      return;
    }

    const card = document.createElement('div');
    card.className = 'card';

    const head = document.createElement('div');
    head.className = 'card__head';
    const label = document.createElement('span');
    label.className = 'card__cup';
    label.textContent = `${this.#cupWord} ${cup}`;
    const price = document.createElement('span');
    price.className = 'card__price';
    price.textContent = this.#money(variant.price);
    head.append(label, price);

    const row = document.createElement('div');
    row.className = 'chosen';

    const img = document.createElement('img');
    img.className = 'chosen__img';
    img.width = 104;
    img.height = 104;
    img.loading = 'lazy';
    img.alt = '';
    if (design.image) img.src = design.image;

    const text = document.createElement('div');
    text.className = 'chosen__text';

    const name = document.createElement('p');
    name.className = 'chosen__name';
    name.textContent = design.title;

    const rows = document.createElement('div');
    rows.className = 'rev__rows';

    /**
     * @param {string} key
     * @param {string} value
     */
    const addRow = (key, value) => {
      const line = document.createElement('div');
      line.className = 'rev__row';
      const k = document.createElement('span');
      k.className = 'rev__key';
      k.textContent = key;
      const v = document.createElement('span');
      v.className = 'rev__val';
      v.textContent = value;
      line.append(k, v);
      rows.append(line);
    };

    addRow('Package', variant.isKit ? variant.title : this.dataset.singleLabel || 'Cup Only');

    if (variant.isKit) {
      const bundle = this.#bundleFor(cup);
      const choice = this.#choice.get(cup);
      for (const selector of bundle?.selectors ?? []) {
        addRow(selector.label, choice?.addons.get(selector.name) ?? '—');
      }
    }

    const edit = document.createElement('button');
    edit.type = 'button';
    edit.className = 'link';
    // Back to stage 2, where both the design and the package for this cup can be
    // changed — and nothing else is lost, because state is never rebuilt.
    edit.dataset.gotoStage = '2';
    edit.textContent = 'Edit';

    text.append(name, rows, edit);
    row.append(img, text);
    card.append(head, row);
    slot.replaceChildren(card);
  }




  #renderBar() {
    const full = this.#cups.reduce((sum, cup) => {
      const v = this.#variantFor(cup);
      return sum + (v ? Number(v.price) : 0);
    }, 0);

    const saving = this.#packSaving();
    const total = Math.max(full - saving, 0);
    const show = this.#packagesDone;

    const barTotal = this.querySelector('[data-bar-total]');
    if (barTotal) barTotal.textContent = show ? this.#money(total.toFixed(2)) : '';

    const barWas = this.querySelector('[data-bar-was]');
    if (barWas instanceof HTMLElement) {
      const showWas = show && saving > 0;
      barWas.hidden = !showWas;
      // Cleared, not merely hidden: a hidden element's text still counts in its
      // parent's textContent, so a stale "$0.00" reads as a real price to
      // anything inspecting the bar.
      barWas.textContent = showWas ? this.#money(full.toFixed(2)) : '';
    }

    // Same number, shown under the title the way a product page shows its price.
    const heroPrice = this.querySelector('[data-hero-price]');
    const heroTotal = this.querySelector('[data-hero-total]');
    const heroWas = this.querySelector('[data-hero-was]');
    const heroNote = this.querySelector('[data-hero-note]');
    const heroSave = this.querySelector('[data-hero-save]');
    if (heroPrice instanceof HTMLElement) {
      heroPrice.hidden = !show;
      if (heroTotal) heroTotal.textContent = this.#money(total.toFixed(2));
      if (heroWas instanceof HTMLElement) {
        heroWas.hidden = saving <= 0;
        heroWas.textContent = saving > 0 ? this.#money(full.toFixed(2)) : '';
      }
      // Was "for both cups", which only made sense in a pack of two.
      if (heroNote) heroNote.textContent = `for ${this.#cupsPhrase}`;
      if (heroSave instanceof HTMLElement) {
        heroSave.hidden = saving <= 0;
        const label = this.dataset.saveLabel || 'You save';
        heroSave.textContent = saving > 0 ? `${label} ${this.#money(saving.toFixed(2))}` : '';
      }
    }

    const lines = this.querySelector('[data-bar-lines]');
    if (lines) {
      lines.textContent = this.#cups.map((cup) => {
        const design = this.#designFor(cup);
        const variant = this.#variantFor(cup);
        if (!design) return `${this.#cupWord} ${cup}: not chosen`;
        const pkg = variant
          ? variant.isKit
            ? variant.title
            : this.dataset.singleLabel || 'Cup Only'
          : 'no package yet';
        // Spell the add-ons out: this is the shopper's last chance to check the
        // pack, and it doubles as a read-out of the _bundle_selection that will
        // be submitted. A silent composition is how the wrong charm reached an
        // order before.
        const selection = variant?.isKit ? this.#bundleSelection(cup) : '';
        const addons = selection ? ` (${selection.split(BUNDLE_GROUP_SEPARATOR).join(', ')})` : '';
        return `${this.#cupWord} ${cup}: ${design.title} — ${pkg}${addons}`;
      }).join('   ·   ');
    }

    const ready = this.querySelector('[data-ready]');
    if (ready instanceof HTMLElement) ready.hidden = !this.#allDone;

    const hint = this.querySelector('[data-bar-hint]');
    const add = this.querySelector('[data-primary]');

    // ADDING IS A REVIEW-STAGE ACTION.
    //
    // Being complete is not the same as being ready to buy. Once both kits were
    // personalised the button lit up on the personalisation stage, so a shopper
    // could add two configured kits without ever seeing the summary of what they
    // had chosen — which, for a pack whose charms and boots cannot be changed
    // after ordering, is the one screen worth insisting on.
    //
    // Cup Only pairs are not held back by this: with no kit to personalise,
    // Continue goes from packages straight to review, so they arrive here
    // immediately.
    // The review summary appears exactly when the pack is complete, so being
    // complete and being ready to buy are now the same moment — the separate
    // review step the gate used to protect no longer exists.
    const canAdd = this.#allDone;

    const message = this.#allDone
      ? this.#packText(this.dataset.readyText || 'Your [pack] is ready!')
      : this.#firstMissing();

    if (hint instanceof HTMLElement) {
      hint.textContent = message;
      hint.dataset.ready = String(canAdd);
    }

    // The button says what to do next and does it — see #primaryAction.
    const action = this.#primaryAction();
    const labelEl = this.querySelector('[data-primary-label]');
    if (labelEl) labelEl.textContent = this.#busy ? 'Adding…' : action.label;

    if (add instanceof HTMLButtonElement) {
      // Disabled WHILE BUSY too. The synchronous #busy guard in #addToCart is
      // the real protection, but a button that still looks pressable during a
      // request invites the second tap in the first place. The aria-busy rule
      // keeps it readable rather than fading it out.
      add.disabled = this.#busy || !action.enabled;
      add.setAttribute('aria-busy', String(this.#busy));
      // Double submission is prevented by state, not by hoping the button is
      // repainted in time: #addToCart returns early while #busy.
      const spinner = this.querySelector('[data-primary-spinner]');
      if (spinner instanceof HTMLElement) spinner.hidden = !this.#busy;
    }
  }

  /**
   * ONE instruction, naming the cup — and always about the cup being worked on.
   *
   * The current cup comes first, then the other. Scanning both in fixed order
   * made the hint disagree with the button: it read "Choose a design for Cup 2"
   * while the button correctly said "Choose Cup 1 charm", which is two different
   * next steps on the same screen.
   *
   * A list of everything outstanding reads as a page full of errors rather than
   * a next step, so this stops at the first thing.
   */
  #firstMissing() {
    const word = this.#cupWord;
    // The cup in hand first, then the other one.
    const order = [this.#tab, this.#tab === 1 ? 2 : 1];

    for (const cup of order) {
      if (!this.#designFor(cup)) return `Choose a design for ${word} ${cup} to continue.`;
      if (!this.#variantFor(cup)) return `Choose a package for ${word} ${cup} to continue.`;
      if (this.#isComplete(cup)) continue;

      const bundle = this.#bundleFor(cup);
      if (!bundle?.configured) return `${word} ${cup} cannot be sold as a kit — choose another package.`;

      const choice = this.#choice.get(cup);
      const missing = bundle.selectors.find((sel) => !choice?.addons.get(sel.name));
      if (missing) return `Choose a ${this.#optionNoun(missing)} for ${word} ${cup} to continue.`;
    }
    return '';
  }

  #renderGrid() {
    const grid = this.querySelector('[data-picker-grid]');
    if (!grid) return;

    const search = this.querySelector('[data-picker-search]');
    const query = (search instanceof HTMLInputElement ? search.value : '').trim().toLowerCase();
    const matches = query ? this.#designs.filter((d) => d.title.toLowerCase().includes(query)) : this.#designs;
    const chosen = this.#activeCup != null ? this.#choice.get(this.#activeCup)?.handle : null;

    grid.replaceChildren(
      ...matches.map((design) => {
        const cheapest = this.#cheapestAvailable(design.variants);

        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'design';
        card.dataset.design = design.handle;
        card.setAttribute('aria-pressed', String(design.handle === chosen));

        const img = document.createElement('img');
        img.className = 'design__img';
        img.loading = 'lazy';
        img.width = 200;
        img.height = 200;
        img.alt = '';
        if (design.image) img.src = design.image;

        const name = document.createElement('span');
        name.className = 'design__name';
        name.textContent = design.title;

        const price = document.createElement('span');
        price.className = 'design__price';
        price.textContent = cheapest ? `From ${this.#money(cheapest.price)}` : '';

        card.append(img, name, price);
        return card;
      })
    );

    const count = this.querySelector('[data-picker-count]');
    if (count) {
      count.textContent = query
        ? `${matches.length} of ${this.#designs.length} designs`
        : `${this.#designs.length} designs`;
    }

    const state = this.querySelector('[data-picker-state]');
    if (state && this.#loaded) {
      state.textContent = matches.length === 0 && query ? `No designs match "${query}".` : '';
    }
  }

  /* ------------------------------------------------------------ payload */

  /**
   * properties[_bundle_selection] for one cup, composed exactly as Simple
   * Bundles' own widget does: every chosen value joined by " <> ".
   * @param {number} cup
   */
  #bundleSelection(cup) {
    const choice = this.#choice.get(cup);
    const bundle = this.#bundleFor(cup);
    if (!choice || !bundle?.configured) return '';
    return bundle.selectors.map((s) => choice.addons.get(s.name) ?? '').join(BUNDLE_GROUP_SEPARATOR);
  }

  /**
   * The /cart/add.js payload.
   *
   * Returns null rather than a partial payload if anything is missing — Shopify
   * silently drops properties with empty values, so a blank charm would vanish
   * from the order with nothing to show it was ever asked for.
   *
   * @returns {{ items: any[] } | null}
   */
  buildPayload() {
    const items = [];

    for (const cup of this.#cups) {
      const variant = this.#variantFor(cup);
      if (!variant) return null;

      /** @type {any} */
      const item = { id: Number(variant.id), quantity: 1 };

      if (variant.isKit) {
        const bundle = this.#bundleFor(cup);
        const choice = this.#choice.get(cup);
        if (!bundle?.configured || !choice) return null;

        /** @type {Record<string, string>} */
        const properties = {};

        for (const selector of bundle.selectors) {
          const value = choice.addons.get(selector.name);
          if (!value) return null;
          // BOTH spellings, deliberately: the app matches on its own long
          // selector id, and the readable duplicate is what a human sees on the
          // order and the packing slip. Dropping either has caused a bad order.
          properties[selector.name] = value;
          properties[selector.label] = value;
        }

        const selection = this.#bundleSelection(cup);
        // Guard the composed value as well as its parts: an empty segment would
        // mean the app cannot split the bundle into component SKUs, which ships
        // as a bare cup.
        if (!selection || selection.split(BUNDLE_GROUP_SEPARATOR).some((part) => !part.trim())) return null;
        properties._bundle_selection = selection;

        item.properties = properties;
      }
      // Cup Only gets no properties at all — not an empty object, which would
      // still be sent.

      items.push(item);
    }

    return { items };
  }

  /**
   * Add the pack, then behave exactly like adding from a product page.
   *
   * NO REDIRECT. The theme's own product form dispatches CartLinesUpdateEvent
   * before the request and resolves its promise after (assets/product-form.js
   * :605-615 and :698 — the multi-item path, which is the same shape as this
   * one). cart-drawer.js:81 listens for action === 'add' and opens the cart,
   * and snippets/rebuy-native-cart.liquid then routes that open to Rebuy Smart
   * Cart. Dispatching the same event is what makes the floating cart appear
   * here for the same reason it appears anywhere else — rather than this file
   * reaching into Rebuy.SmartCart.show() itself and having to reimplement its
   * readiness and skip_open guards.
   *
   * The promise is settled on EVERY path. A listener that is still waiting on it
   * would leave the cart drawer mid-open forever.
   */
  async #addToCart() {
    if (this.#busy || !this.#allDone) return;

    const payload = this.buildPayload();
    const hint = this.querySelector('[data-bar-hint]');
    if (!payload) {
      if (hint instanceof HTMLElement) {
        hint.textContent = 'Something is missing from your pack. Please check your choices and try again.';
      }
      return;
    }

    this.#busy = true;
    this.#renderBar();
    if (hint instanceof HTMLElement) hint.textContent = this.#packText('Adding your [n]-Pack…');

    const deferred = CartLinesUpdateEvent.createPromise();
    const itemCount = payload.items.reduce((sum, item) => sum + item.quantity, 0);

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        // 'product' is the context the cart drawer treats as "opened from a
        // product add", which is the behaviour being matched.
        context: 'product',
        lines: payload.items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      const body = await res.json().catch(() => null);
      // /cart/add.js answers 200 with a `status` field on a soft failure, so the
      // status code alone is not enough.
      if (!res.ok || body?.status) {
        throw new Error(body?.description || body?.message || `HTTP ${res.status}`);
      }

      // /cart/add.js returns the added lines, not the whole cart, and listeners
      // want the cart — the same reason the theme calls its own #refreshCart().
      const cart = await fetch('/cart.js').then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'two-pack-builder',
          sourceId: this.dataset.uid ?? 'two-pack',
          itemCount,
        },
      });

      // Start clean. The cart is now open over this page, and leaving the
      // finished configuration sitting here would let a second click add the
      // same pack again.
      this.#choice.clear();
      this.#tab = 1;
      this.#busy = false;
      this.#render();

      const freshHint = this.querySelector('[data-bar-hint]');
      if (freshHint instanceof HTMLElement) {
        freshHint.textContent = this.#packText('Added to your cart. Build another [n]-pack below.');
      }
    } catch (error) {
      console.error('[two-pack] add to cart failed', error);
      deferred.reject(error instanceof Error ? error : new Error(String(error)));

      if (hint instanceof HTMLElement) {
        hint.textContent = this.#packText('Could not add your [n]-Pack: ') + (error instanceof Error ? error.message : 'please try again');
      }
      this.#busy = false;
      this.#renderBar();
    }
  }

  /* ------------------------------------------------------------- events */

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const scroll = target.closest('[data-kit-scroll]');
    if (scroll instanceof HTMLElement) {
      const cards = scroll.closest('[data-kit-rail]')?.querySelector('.kit-cards');
      if (cards instanceof HTMLElement) {
        cards.scrollBy({
          left: Number(scroll.dataset.kitScroll) * cards.clientWidth * 0.8,
          behavior: this.#reducedMotion ? 'auto' : 'smooth',
        });
      }
      return;
    }

    const size = target.closest('[data-pack-size]');
    if (size instanceof HTMLElement) {
      this.#setPackSize(Number(size.dataset.packSize));
      return;
    }

    const done = target.closest('[data-cup-done]');
    if (done instanceof HTMLElement) {
      this.#saveCup(Number(done.dataset.cupDone));
      return;
    }

    const thumb = target.closest('[data-gallery-thumb]');
    if (thumb instanceof HTMLElement && thumb.dataset.galleryThumb) {
      this.#activeImage.set(this.#tab, thumb.dataset.galleryThumb);
      this.#renderMedia();
      return;
    }

    const tab = target.closest('[data-cup-tab]');
    if (tab instanceof HTMLElement) {
      this.#goTab(Number(tab.dataset.cupTab));
      return;
    }

    const opener = target.closest('[data-open-picker]');
    if (opener instanceof HTMLElement) {
      this.#openPicker(Number(opener.dataset.openPicker));
      return;
    }

    if (target.closest('[data-close-picker]')) {
      this.#dialog('[data-picker]')?.close();
      return;
    }

    if (target.closest('[data-open-video]')) {
      this.#openVideo();
      return;
    }

    if (target.closest('[data-close-video]')) {
      this.#dialog('[data-video-dialog]')?.close();
      return;
    }

    if (target.closest('[data-primary]')) {
      // Whatever the button currently means. Resolved at click time so it can
      // never act on a stale intention.
      this.#primaryAction().act();
      return;
    }

    const card = target.closest('[data-design]');
    if (card instanceof HTMLElement) this.#chooseDesign(card.dataset.design);
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    // The cup comes from the slot the input sits in, so a choice can never be
    // written against the other cup.
    const cup = Number(input.closest('[data-cup]')?.getAttribute('data-cup'));
    const choice = this.#choice.get(cup);
    if (!choice) return;

    if (input.dataset.addonSelector) {
      const wasComplete = this.#isComplete(cup);
      choice.addons.set(input.dataset.addonSelector, input.value);

      // A full re-render here would rebuild the option grid under the shopper's
      // thumb mid-choice. The radio already shows its own checked state, so only
      // what actually changed is re-rendered.
      // Only what actually changed: a full re-render would rebuild the option
      // grid under the shopper's thumb mid-choice.
      this.#renderTabs();
      this.#renderBar();

      // Only once this cup has just become complete does anything move.
      if (!wasComplete && this.#isComplete(cup)) {
        this.#render();
        this.#revealNext(cup);
      }
      return;
    }

    if (input.dataset.tierInput != null) {
      const tier = input.value === 'full' ? 'full' : 'single';

      // Moving to the single CLEARS this cup's charm and boot. buildPayload
      // already omits properties for a non-kit line, so this is belt and braces
      // — but leaving the data behind means a later switch back silently
      // restores choices the shopper never re-made, and a stale charm is
      // exactly the failure this page is careful about.
      const addons = tier === 'full' ? choice.addons : new Map();

      this.#choice.set(cup, { ...choice, tier, addons });
      // The kit and the single often photograph differently, so the gallery
      // follows the package as well as the design.
      this.#activeImage.delete(cup);
      // Changing the package changes whether stage 3 applies at all.
      this.#render();
      this.#revealNext(cup);
    }
  };

  /** @param {Event} event */
  #onInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-picker-search]')) {
      this.#renderGrid();
    }
  };

  /* ------------------------------------------------------------ actions */


  /**
   * Put focus back where it belongs after a dialog closes.
   *
   * Preferred target is whatever opened it; when that element has been replaced
   * by the re-render a choice causes, fall back to the cup card the choice
   * belongs to, and then to the primary action. Anything is better than the
   * document top, which is where the browser leaves it.
   */
  #restoreFocus() {
    const previous = this.#returnFocusTo;
    this.#returnFocusTo = null;

    /** @type {(HTMLElement | null)[]} */
    const candidates = [
      previous && previous.isConnected ? previous : null,
      this.#activeCup != null
        ? /** @type {HTMLElement | null} */ (this.#slot('design', this.#activeCup)?.querySelector('button'))
        : null,
      /** @type {HTMLElement | null} */ (this.querySelector('[data-primary]')),
    ];

    for (const el of candidates) {
      if (el && typeof el.focus === 'function') {
        el.focus({ preventScroll: true });
        return;
      }
    }
  }

  /** @param {number} cup */
  async #openPicker(cup) {
    if (!Number.isInteger(cup)) return;
    const picker = this.#dialog('[data-picker]');
    if (!picker) return;

    this.#returnFocusTo =
      document.activeElement instanceof HTMLElement && this.contains(document.activeElement)
        ? document.activeElement
        : null;
    this.#activeCup = cup;
    picker.showModal();
    // showModal() does not lock this theme at >=990px, where the scroll
    // container is .page-wrapper rather than the document (base.css:32-59).
    // Going through the theme's own helper — which keeps an owner Set — is what
    // stops this clobbering the cart drawer's lock when both are open.
    lockScroll(picker);

    const search = this.querySelector('[data-picker-search]');
    if (search instanceof HTMLInputElement) search.value = '';

    await this.#load();
    this.#renderGrid();
  }

  #openVideo() {
    this.#returnFocusTo =
      document.activeElement instanceof HTMLElement && this.contains(document.activeElement)
        ? document.activeElement
        : null;
    const dialog = this.#dialog('[data-video-dialog]');
    const slot = dialog?.querySelector('[data-video-slot]');
    const url = dialog?.dataset.videoUrl;
    if (!dialog || !slot || !url) return;

    // Built on demand: embedding the player up front would load a third party
    // on every visit for a video most shoppers never open.
    if (!slot.firstChild) {
      const frame = document.createElement('iframe');
      frame.src = url;
      frame.title = this.#packText('How the [n]-pack builder works');
      frame.loading = 'lazy';
      frame.allow = 'accelerometer; clipboard-write; encrypted-media; picture-in-picture; fullscreen';
      frame.setAttribute('allowfullscreen', '');
      slot.append(frame);
    }

    dialog.showModal();
    lockScroll(dialog);
  }

  /** @param {string | undefined} handle */
  async #chooseDesign(handle) {
    if (!handle || this.#activeCup == null) return;
    const cup = this.#activeCup;

    const design = this.#designs.find((d) => d.handle === handle);
    if (!design) return;

    const previous = this.#choice.get(cup);

    // DEFAULT TO THE KIT, as the product page does — its variants list the Full
    // CrewCup Kit first, so a product page opens with it already selected.
    // Completion still requires a charm and a boot, so defaulting the package
    // cannot let a half-configured kit through; it only saves a tap on the
    // common path.
    //
    // A fresh addons map unless the design is unchanged: carrying the previous
    // design's charm over would submit a value this design's bundle does not
    // offer. Keeping them when the design is the same is what makes going back
    // lossless.
    // The package the shopper already chose is KEPT — it may have been chosen
    // before the design, and it is a tier rather than a variant id precisely so
    // it survives this. #variantFor resolves it against the new design, so the
    // ids that reach the cart are always this product's.
    const sameDesign = previous?.handle === handle;
    this.#choice.set(cup, {
      handle,
      // Whatever package the shopper already picked is kept; none is invented.
      tier: previous?.tier ?? null,
      // Carrying the previous design's charm over would submit a value the new
      // design's bundle does not offer; keeping them on the same design is what
      // makes going back lossless.
      addons: sameDesign && previous ? previous.addons : new Map(),
    });
    this.#activeImage.delete(cup);

    this.#dialog('[data-picker]')?.close();
    this.#render();
    this.#revealNext(cup);

    // Then this design's own charm/boot options, so the kit package can be
    // blocked if the design has no bundle registered.
    await this.#loadBundle(handle);
    this.#render();
  }
}

if (!customElements.get('two-pack-builder')) {
  customElements.define('two-pack-builder', TwoPackBuilder);
}
